import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../../../store';
import { Users } from 'lucide-react';
import { Pagination2, ShimmerBlock } from '../../../common';
import { useGetCompletedResponsesQuery, useGetPendingResponsesQuery } from '../../../../store/apis/survey.api';
import { setPage, setLimit } from '../../../../store/slices/surveySlice';
import { getInitials } from '../../../common/Table2';
import { avatarGradientToken } from '../../../../utils/surveyInsights';
import { formatDDMMYYYY } from '../../../../utils/timeUtils';
import type { CompletedResponse, PendingResponse } from '../types';

export interface RespondentSelection {
  kind: 'completed' | 'pending';
  id: string;
  name: string;
  sub: string;
  submittedLabel: string;
}

interface RespondentsPanelProps {
  surveyId: string;
  selected: RespondentSelection | null;
  onSelect: (selection: RespondentSelection) => void;
}

/** Figma respondents panel (dc.html:3015): selectable list w/ status pills. */
const RespondentsPanel: React.FC<RespondentsPanelProps> = ({ surveyId, selected, onSelect }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { page, limit } = useSelector((state: RootState) => state.survey);

  const completedQuery = useGetCompletedResponsesQuery({ surveyId, page, pageSize: limit });
  const pendingQuery = useGetPendingResponsesQuery({ surveyId, page, pageSize: limit });

  const completed: CompletedResponse[] = completedQuery.data?.data ?? [];
  const pending: PendingResponse[] = pendingQuery.data?.data ?? [];
  const completedTotal = completedQuery.data?.pagination?.total ?? 0;
  const pendingTotal = pendingQuery.data?.pagination?.total ?? 0;
  const loading = completedQuery.isLoading || pendingQuery.isLoading;

  const rows = useMemo<RespondentSelection[]>(
    () => [
      ...completed.map((r) => ({
        kind: 'completed' as const,
        id: r.responseId,
        name: r.employee.name,
        sub: r.employee.employeeCode || r.employee.email || '',
        submittedLabel: `submitted ${formatDDMMYYYY(r.submittedDate) ?? r.submittedDate}`,
      })),
      ...pending.map((r) => ({
        kind: 'pending' as const,
        id: r.id,
        name: r.employee.name,
        sub: r.employee.employeeCode || r.employee.email || '',
        submittedLabel: `assigned ${formatDDMMYYYY(r.assignedDate) ?? r.assignedDate}`,
      })),
    ],
    [completed, pending]
  );

  // Default-select the first respondent once data arrives
  useEffect(() => {
    if (!selected && rows.length > 0) {
      onSelect(rows[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, selected]);

  const total = completedTotal + pendingTotal;

  return (
    <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] p-3.5 overflow-hidden">
      <div className="flex items-center gap-2 px-1 pb-3">
        <Users className="w-4 h-4 text-[var(--color-text-tertiary)]" />
        <span className="text-body-md font-bold text-[var(--color-text-primary)]">Respondents</span>
        {!loading && total > 0 && (
          <span className="text-caption font-bold text-[var(--color-text-secondary)] bg-[var(--color-bg-muted)] px-2 py-0.5 rounded-full">
            {total}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-1">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-[11px_14px]">
              <ShimmerBlock className="w-[34px] h-[34px] rounded-[10px] flex-none" />
              <div className="flex-1 space-y-2">
                <ShimmerBlock className="h-3.5 w-2/3 rounded" />
                <ShimmerBlock className="h-3 w-1/3 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : rows.length > 0 ? (
        <div className="flex flex-col gap-1">
          {rows.map((r) => {
            const on = selected?.id === r.id && selected?.kind === r.kind;
            const done = r.kind === 'completed';
            return (
              <button
                key={`${r.kind}-${r.id}`}
                type="button"
                onClick={() => onSelect(r)}
                aria-pressed={on}
                className={`flex items-center gap-[11px] p-[11px_14px] rounded-[12px] cursor-pointer transition-all border text-left w-full focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none ${
                  on
                    ? "border-[var(--color-primary-600)] bg-[var(--color-primary-50)]"
                    : "border-transparent bg-transparent hover:bg-[var(--color-bg-muted)]"
                }`}
              >
                <span
                  className={`w-[34px] h-[34px] rounded-[10px] flex-none grid place-items-center text-white font-bold text-body-xs ${avatarGradientToken(r.name)}`}
                >
                  {getInitials(r.name)}
                </span>
                <span className="flex-1 min-w-0">
                  <span
                    className={`block text-label font-bold truncate ${
                      on ? "text-[var(--color-primary-700)]" : "text-[var(--color-text-primary)]"
                    }`}
                  >
                    {r.name}
                  </span>
                  <span className="block text-caption text-[var(--color-text-tertiary)] truncate">
                    {r.sub}
                  </span>
                </span>
                <span
                  className={`inline-flex items-center px-2 py-[3px] rounded-full text-badge font-bold whitespace-nowrap flex-none ${
                    done
                      ? "bg-[var(--color-success-light)] text-[var(--color-success-dark)]"
                      : "bg-[var(--color-warning-light)] text-[var(--color-warning-dark)]"
                  }`}
                >
                  {done ? 'Complete' : 'Partial'}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-10 px-4">
          <p className="text-body-md font-bold text-[var(--color-text-primary)]">No respondents yet</p>
          <p className="text-body-xs text-[var(--color-text-secondary)] mt-1">
            Responses will appear here once submitted.
          </p>
        </div>
      )}

      {total > limit && (
        <div className="pt-3 mt-1 border-t border-[var(--color-border-tertiary)]">
          <Pagination2
            total={total}
            page={page}
            pageSize={limit}
            pageSizeOptions={[5, 10, 20, 50]}
            onPageChange={(p) => dispatch(setPage(p))}
            onPageSizeChange={(l) => {
              dispatch(setLimit(l));
              dispatch(setPage(1));
            }}
            autoHide
          />
        </div>
      )}
    </div>
  );
};

export default RespondentsPanel;
